/**
 * Shared form validation helpers.
 * Use with useFormValidation or validateForm(schema, values).
 */

const isEmpty = (value) =>
  value === undefined ||
  value === null ||
  (typeof value === "string" && value.trim() === "") ||
  (Array.isArray(value) && value.length === 0);

export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Max digits allowed for mobile fields. */
export const NATIONAL_PHONE_MAX_DIGITS = 10;

/** Digits only, max 10. Strips +91 / 0 prefix when extra digits are present. */
export const sanitizeNationalPhone = (value) => {
  let digits = String(value ?? "").replace(/\D/g, "");
  if (!digits) return "";

  if (digits.startsWith("91") && digits.length >= 12) {
    digits = digits.slice(2);
  } else if (digits.startsWith("0") && digits.length >= 11) {
    digits = digits.slice(1);
  }

  if (digits.length > NATIONAL_PHONE_MAX_DIGITS) {
    digits = digits.slice(-NATIONAL_PHONE_MAX_DIGITS);
  }
  return digits;
};

/** Field must have a non-empty value. */
export const required =
  (message = "This field is required.") =>
  (value) =>
    isEmpty(value) ? message : "";

/** Valid email format (skips empty — pair with required). */
export const email =
  (message = "Enter a valid email address.") =>
  (value) => {
    if (isEmpty(value)) return "";
    return EMAIL_REGEX.test(String(value).trim()) ? "" : message;
  };

/** Mobile: 10 digits, Indian series 6–9. */
export const mobile =
  (message = "Enter a valid 10-digit mobile number.") =>
  (value) => {
    if (isEmpty(value)) return "";
    const digits = sanitizeNationalPhone(value);
    if (!/^[6-9]\d{9}$/.test(digits)) return message;
    return "";
  };

/** Minimum string length. */
export const minLength =
  (min, message) =>
  (value) => {
    if (isEmpty(value)) return "";
    const msg = message || `Must be at least ${min} characters.`;
    return String(value).length >= min ? "" : msg;
  };

/** Maximum string length. */
export const maxLength =
  (max, message) =>
  (value) => {
    if (isEmpty(value)) return "";
    const msg = message || `Must be at most ${max} characters.`;
    return String(value).length <= max ? "" : msg;
  };

/** Value must equal another field (e.g. confirm password). */
export const matchField =
  (otherField, message = "Values do not match.") =>
  (value, allValues = {}) => {
    if (isEmpty(value) && isEmpty(allValues[otherField])) return "";
    return value === allValues[otherField] ? "" : message;
  };

/**
 * Password strength rules used across the app.
 * Returns first failing message, or "" if valid.
 */
export const password =
  (options = {}) =>
  (value) => {
    const {
      min = 8,
      requireUppercase = true,
      requireDigit = true,
      requireSpecial = true,
    } = options;

    if (isEmpty(value)) return "";

    if (String(value).length < min) {
      return `Password must be at least ${min} characters.`;
    }
    if (requireUppercase && !/[A-Z]/.test(value)) {
      return "Password must contain at least one capital letter.";
    }
    if (requireDigit && !/[0-9]/.test(value)) {
      return "Password must contain at least one digit.";
    }
    if (requireSpecial && !/[!@#$%^&*(),.?":{}|<>]/.test(value)) {
      return "Password must contain at least one special character.";
    }
    return "";
  };

/** Custom predicate validator. */
export const custom =
  (fn, message = "Invalid value.") =>
  (value, allValues) =>
    fn(value, allValues) ? "" : message;

/**
 * Run a list of validators against one field.
 * @returns first error message or ""
 */
export const runValidators = (value, validators = [], allValues = {}) => {
  for (const validator of validators) {
    if (typeof validator !== "function") continue;
    const message = validator(value, allValues);
    if (message) return message;
  }
  return "";
};

/**
 * Validate all fields from a schema.
 * schema: { fieldName: [validatorFn, ...] }
 * @returns { isValid, errors }
 */
export const validateForm = (values = {}, schema = {}) => {
  const errors = {};
  let isValid = true;

  Object.keys(schema).forEach((field) => {
    const message = runValidators(values[field], schema[field], values);
    if (message) {
      errors[field] = message;
      isValid = false;
    }
  });

  return { isValid, errors };
};

/**
 * Build TextField-friendly props from validation state.
 */
export const getFieldErrorProps = (errors = {}, touched = {}, field, helperText = "") => {
  const showError = Boolean(touched[field] && errors[field]);
  return {
    error: showError,
    helperText: showError ? errors[field] : helperText,
  };
};

const formValidation = {
  required,
  email,
  mobile,
  minLength,
  maxLength,
  matchField,
  password,
  custom,
  runValidators,
  validateForm,
  getFieldErrorProps,
  sanitizeNationalPhone,
  EMAIL_REGEX,
  NATIONAL_PHONE_MAX_DIGITS,
};

export default formValidation;
